import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatQty } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { useTapOnce } from '../ui/useTapOnce';
import { sendOnce } from '../tickets/sendOnce';
import { Button } from '../ui/Button';
import { QtyStepper } from '../ui/QtyStepper';
import { amountOf } from '../money/BillPartsSheet';

/**
 * Write off some of a part (Inventory v2 12c, decision 3, prototype v3 verdict B), or hours of a
 * time entry (ticket 13, verdict A): how much, at most what is left to bill, and why, which is
 * required. It never shows on an invoice; its cost stays on the job. The server checks the amount
 * against what is left. One save per tap. Hours step by a quarter.
 */
export function WriteOffEditor({
  part,
  time,
  onDone,
}: {
  part?: { jobCostId: string; item: { name: string }; split: { left: number } | null };
  /** Hours of a time entry instead (ticket 13): `name` says whose time and which day. */
  time?: { timeEntryId: string; name: string; left: number };
  onDone: () => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const lock = useTapOnce();
  const name = time ? time.name : part!.item.name;
  const left = Math.round((time ? time.left : (part!.split?.left ?? 0)) * 100);
  const unit = time ? ' h' : '';
  const [qty, setQty] = useState(formatQty(left));
  const [reason, setReason] = useState('');
  const done = {
    onSettled: () => lock.release(),
    onSuccess: () => {
      void qc.invalidateQueries();
      onDone();
    },
  };
  const savePart = useMutation(trpc.partsBilling.writeOff.mutationOptions(done));
  const saveTime = useMutation(trpc.partsBilling.writeOffTime.mutationOptions(done));
  const save = time ? saveTime : savePart;
  const n = amountOf(qty, left);
  const ok = n !== null && n > 0;
  const why = reason.trim();
  const id = time ? `write-off-time-${time.timeEntryId}` : `write-off-${part!.jobCostId}`;
  return (
    <div className="flex basis-full flex-wrap items-center gap-2 rounded-[var(--radius-md)] border border-border bg-surface-2 p-2 text-sm">
      <span className="text-xs font-semibold text-muted-foreground">Write off</span>
      <QtyStepper
        value={qty}
        min={1}
        max={left}
        locked={save.isPending}
        label={time ? `Hours of ${name} to write off` : `How many ${name} to write off`}
        of={name}
        errorId={ok ? undefined : `${id}-qty`}
        onChange={setQty}
        {...(time ? { step: 25, stepName: 'A quarter hour' } : {})}
      />
      <input
        id={`${id}-reason`}
        autoFocus
        className="h-9 min-w-[12rem] flex-1 rounded-[var(--radius-sm)] border border-border bg-surface px-2"
        placeholder="Why it won't be billed (required)"
        aria-label={`Why ${name} won't be billed`}
        aria-required="true"
        value={reason}
        maxLength={500}
        readOnly={save.isPending}
        onChange={(e) => setReason(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onDone();
        }}
      />
      {ok ? null : (
        <span
          id={`${id}-qty`}
          className="basis-full text-xs text-[var(--sw-danger-text)]"
        >
          Pick between 0.01 and {formatQty(left)}
          {unit}, in hundredths at most.
        </span>
      )}
      <span className="basis-full text-xs text-muted-foreground">
        It never shows on an invoice. Its cost stays on the job.
      </span>
      {why === '' ? (
        <span id={`${id}-need`} className="basis-full text-xs text-muted-foreground">
          A write-off needs a reason.
        </span>
      ) : null}
      {save.error ? (
        <span role="alert" className="basis-full text-xs text-[var(--sw-danger-text)]">
          {save.error.message}
        </span>
      ) : null}
      <span className="ml-auto flex gap-2">
        <Button variant="ghost" onClick={onDone} disabled={save.isPending}>
          Cancel
        </Button>
        <Button
          disabled={!ok || why === '' || save.isPending}
          aria-describedby={why === '' ? `${id}-need` : ok ? undefined : `${id}-qty`}
          onClick={() =>
            ok &&
            (time
              ? sendOnce(lock, saveTime.mutate, {
                  timeEntryId: time.timeEntryId,
                  hours: n / 100,
                  reason: why,
                })
              : sendOnce(lock, savePart.mutate, {
                  jobCostId: part!.jobCostId,
                  qty: n / 100,
                  reason: why,
                }))
          }
        >
          {save.isPending
            ? 'Writing off…'
            : `Write off ${ok ? `${formatQty(n)}${unit}` : ''}`.trim()}
        </Button>
      </span>
    </div>
  );
}
